// Client-side handling of 401 responses from authenticated admin API calls
// (BUG-802, RAC 3). Turns a stale/expired staff session into a sign-in redirect
// instead of a raw "Unauthorized" error. Wired into the shared api client's
// `onUnauthorized` hook (see `@/api-client/typed-client`), so it fires for
// every typed call — including the `.raw()` escape hatch.
//
// Admin pages are all gated by middleware (unauthenticated visitors never load
// a dashboard page), so a client 401 means a session that died mid-action — the
// one static guard is the sign-in page itself, so a 401 there can't loop.
//
// BUG-1182: a *single* 401 is NOT proof the session is gone. The staff_users
// read on the auth hot path blips transiently (a DB hiccup at Better Auth's 60s
// cookieCache refresh yields a momentarily-null session → a 401 for a session
// that's actually still valid). Redirecting on that unconfirmed 401 bounced
// logged-in operators mid-session (incl. a clerk off a live auction). So the
// handler now CORROBORATES: it re-reads /api/auth/me and only navigates when
// that call also returns 401. A 200 (session fine — the 401 was transient), a
// 5xx, or a network error all leave the operator where they are.

import { safeReturnUrl } from '@auctogon/core/validation/safe-return-url';

import { isSignOutNavigationUnderway } from './sign-out-navigation';

/** Every admin auth route lives under `/auth/` (see src/middleware.ts). */
function isAuthPath(pathname: string): boolean {
  return pathname === '/auth' || pathname.startsWith('/auth/');
}

/**
 * Pure decision: should a 401 route the operator to sign-in?
 *
 * @returns `true` unless we're already on an auth route (loop guard).
 */
export function shouldRedirectOnUnauthorized(opts: { currentPath: string }): boolean {
  return !isAuthPath(opts.currentPath);
}

/**
 * Build the sign-in URL with a `returnUrl` back to the current page — same
 * convention the middleware uses for full-page-navigation gating.
 */
export function buildAdminSignInUrl(opts: { pathname: string; search: string }): string {
  // Guard against open redirect (CWE-601): the returnUrl is same-origin by
  // construction (built from window.location), but route it through the shared
  // validator so the invariant is enforced structurally, not by assumption.
  const returnUrl = safeReturnUrl(`${opts.pathname}${opts.search}`);
  return `/auth/sign-in?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/**
 * Re-read the canonical session endpoint to decide whether a 401 was real.
 *
 * Uses a plain `fetch` (NOT the typed client) so it doesn't re-enter
 * `onUnauthorized` and recurse. Only a confirmed **401** means "logged out";
 * a 200 (session still valid), a 5xx, or a thrown network error are all treated
 * as transient — the operator is NOT redirected.
 *
 * @returns `true` iff `/api/auth/me` confirms the session is gone (HTTP 401).
 * @sideEffect One `GET /api/auth/me` network read.
 */
async function isSessionConfirmedGone(): Promise<boolean> {
  try {
    // eslint-disable-next-line apifetch/no-inline-api-fetch -- MUST be a raw fetch, not the typed client: routing /api/auth/me through `api.*` would re-enter this onUnauthorized handler on a 401 and recurse. Same carve-out as the realtime catch-up fetcher (banned-patterns.md).
    const res = await fetch('/api/auth/me', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-store' },
    });
    return res.status === 401;
  } catch (error) {
    // Network error — indistinguishable from a transient blip. Fail safe: don't
    // nuke a possibly-valid session on the strength of one failed probe. Log it
    // (mirrors fetchSessionUser) so a persistently-broken probe still surfaces
    // in the logs rather than silently swallowing every corroboration.
    // eslint-disable-next-line no-console -- browser-side module: client console goes to the browser devtools/GCP request logs; the server-only createLogger doesn't apply here.
    console.error('[unauthorized-redirect] session corroboration failed', error);
    return false;
  }
}

// One in-flight corroboration at a time: a burst of 401s (a page firing many
// authenticated calls at once) must trigger a single /api/auth/me re-check and
// at most one redirect, not N.
let corroborationInFlight = false;

/**
 * `onUnauthorized` handler for the admin api client. On a 401, CORROBORATES the
 * session via `/api/auth/me` before routing to `/auth/sign-in` — only a
 * confirmed-gone session redirects (BUG-1182). A transient 401 leaves the
 * operator on their page.
 *
 * @sideEffect Reads `/api/auth/me`; navigates the browser to the sign-in page
 *   only when the session is confirmed gone.
 */
export async function handleAdminUnauthorized(): Promise<void> {
  if (typeof window === 'undefined') return;
  // A sign-out in progress is already loading sign-in; a second load would cancel it (BUG-2233).
  if (isSignOutNavigationUnderway()) return;
  if (!shouldRedirectOnUnauthorized({ currentPath: window.location.pathname })) return;
  if (corroborationInFlight) return;

  corroborationInFlight = true;
  try {
    if (!(await isSessionConfirmedGone())) return; // transient 401 — stay put
    // The probe takes a round trip: a sign-out may have begun meanwhile (BUG-2233).
    if (isSignOutNavigationUnderway()) return;
    // Re-read location after the await (the operator may have navigated), and
    // re-check the loop guard in case they're now on an auth route.
    const { pathname, search } = window.location;
    if (!shouldRedirectOnUnauthorized({ currentPath: pathname })) return;
    // Full navigation (not router.push): a dead session should clear all stale
    // in-memory client state, and the api-client singleton has no Next router.
    window.location.assign(buildAdminSignInUrl({ pathname, search }));
  } finally {
    corroborationInFlight = false;
  }
}
